import React, { useState } from 'react'
import { AdCreative, AdCreativeType } from '@marketing-milk/interfaces'
import {
  isAdImage,
  isAdVideo,
  isCarouselCreative,
  isDynamicCreative,
  isSingleCreative,
} from '../../campaigns/helpers/campaign.helpers'

type Props = {
  creative: AdCreative
  className?: string
  objectFit?: 'cover' | 'contain'
}
export const CreativeMedia = ({ creative: given, className, objectFit = 'cover' }: Props) => {
  // A per-placement ad's card shows its Feed cut, as a single ad would.
  const feed = given.format === AdCreativeType.per_placement ? given.placementCuts?.feed : undefined
  const creative = feed
    ? {
        ...given,
        singleImage: feed.mediaType === 'image' ? feed.image : undefined,
        singleVideo: feed.mediaType === 'video' ? feed.video : undefined,
      }
    : given
  const [brokenLink, setBrokenLink] = useState(false)

  const tailwindHeight = className || 'h-64 w-full bg-gray-200'
  const fitClass = objectFit === 'contain' ? 'object-contain' : 'object-cover'

  if (brokenLink) {
    if (creative.singleVideo?.thumbnail) {
      return (
        <img
          src={creative.singleVideo.thumbnail}
          alt="creative video thumbnail"
          className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
        />
      )
    }

    if (isCarouselCreative(creative) && creative.carouselSlides[0].mediaType === 'video') {
      return (
        <img
          src={creative.carouselSlides[0].video.thumbnail}
          alt="creative video thumbnail"
          className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
        />
      )
    }

    if (isDynamicCreative(creative)) {
      const creativeContent = [...creative.dynamicImages, ...creative.dynamicVideos]
      const previewData = creativeContent[0]
      if (isAdVideo(previewData)) {
        return (
          <img
            src={previewData.thumbnail}
            alt="creative thumbnail"
            className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
          />
        )
      }
    }

    return (
      <p
        className={`font-bold truncate text-red-500 ${tailwindHeight} w-full flex items-center justify-center truncate`}
      >
        <i className="fa fa-exclamation-triangle mr-2 text-red-400" />{' '}
        <p className="truncate">Link Broken In Marketing Milk</p>
      </p>
    )
  }
  if (isSingleCreative(creative)) {
    if (creative.singleImage)
      return (
        <img
          src={creative.singleImage.url}
          alt={creative.singleImage.name}
          onError={() => setBrokenLink(true)}
          className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
        />
      )

    if (creative.singleVideo) {
      // Prefer our durable stored copy over the expiring Facebook source.
      const videoSource = creative.singleVideo.storedSource ?? creative.singleVideo.source
      const poster = creative.singleVideo.thumbnail

      if (videoSource)
        return (
          <video
            autoPlay
            preload="auto"
            muted
            loop
            poster={poster}
            onError={() => setBrokenLink(true)}
            playsInline
            src={videoSource}
            className={`${tailwindHeight} rounded-t-lg ${fitClass}`}
          />
        )

      // No playable source: show the poster if we have one, otherwise a clear
      // unavailable state rather than a blank <video> or generic "No Media Added".
      if (poster)
        return (
          <img
            src={poster}
            alt="creative video thumbnail"
            onError={() => setBrokenLink(true)}
            className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
          />
        )

      return (
        <div
          className={`font-bold text-yellow-500 w-full flex ${tailwindHeight} items-center justify-center`}
        >
          <i className="fa fa-exclamation-triangle mr-2 text-yellow-500" /> Video unavailable
        </div>
      )
    }
  }

  if (isCarouselCreative(creative)) {
    if (creative.carouselSlides && creative.carouselSlides[0].mediaType === 'image') {
      return (
        <img
          src={creative.carouselSlides[0].image.url}
          alt={creative.carouselSlides[0].image.name}
          onError={() => setBrokenLink(true)}
          className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
        />
      )
    }

    if (creative.carouselSlides && creative.carouselSlides[0].mediaType === 'video') {
      return (
        <video
          autoPlay
          preload="auto"
          muted
          loop
          onError={() => setBrokenLink(true)}
          playsInline
          src={creative.carouselSlides[0].video.source ?? ''}
          className={`${tailwindHeight} rounded-t-lg ${fitClass}`}
        />
      )
    }
  }

  if (isDynamicCreative(creative)) {
    const creativeContent = [...creative.dynamicImages, ...creative.dynamicVideos]
    const previewData = creativeContent[0]
    if (previewData) {
      if (isAdImage(previewData)) {
        return (
          <img
            src={previewData.url}
            alt={previewData.name}
            onError={() => setBrokenLink(true)}
            className={`${tailwindHeight} ${fitClass} rounded-t-lg`}
          />
        )
      }
      if (isAdVideo(previewData)) {
        return (
          <video
            autoPlay
            preload="auto"
            muted
            loop
            onError={() => setBrokenLink(true)}
            playsInline
            src={previewData.source ?? ''}
            className={`${tailwindHeight} rounded-t-lg ${fitClass}`}
          />
        )
      }
    }
  }

  return (
    <div
      className={`font-bold text-yellow-500 w-full flex ${tailwindHeight} items-center justify-center `}
    >
      <i className="fa fa-exclamation-triangle mr-2 text-yellow-500" /> No Media Added
    </div>
  )
}
